import React, { useEffect } from "react"
import { useHookstate, type State } from "@hookstate/core"
import { Button, Grid } from "@mui/material"
import FilterAutocompleteFetch from "~/components/FilterAutocompleteFetch"
import SelectBasic, { type OptionType } from "~/components/SelectBasic"
import type IUser from "~/features/User/interfaces/IUser"
import { useLang } from "~/hooks/useLang"
import type IDevice from "~/features/Device/interfaces/IDevice"
import type { CheckAvailabilityResponse, CrewList } from "../../interfaces"

interface CrewCompositionCreationFormProps {
  readonly crewPositionId: State<string | null>;
  readonly focusChange: State<boolean>;
  readonly resetValues: State<boolean>;
  readonly userId: State<number | null>;
  readonly device?: State<IDevice | null>;
  readonly crewPositionOptions: readonly OptionType[];
  readonly crewList: State<CrewList[]>;
  readonly activeStep: State<number>;
  readonly checkAvailabilityResult?: State<CheckAvailabilityResponse | null>;
  readonly onAddCrewMember: (user: IUser, position: OptionType) => boolean;
  readonly disabledFields?: boolean;
}

export default function CrewCompositionCreationForm({
  crewPositionId,
  focusChange,
  resetValues,
  userId,
  crewPositionOptions,
  checkAvailabilityResult,
  activeStep,
  disabledFields,
  onAddCrewMember
}: CrewCompositionCreationFormProps) {
  const { translate } = useLang();
  const getFullObjectUser = useHookstate<IUser | null>(null);
  const getFullObjectPosition = useHookstate<OptionType | null>(null);
  const errorField = activeStep.get() === 2 ? Boolean(checkAvailabilityResult?.get()?.error) : false

  useEffect(() => {
    const positionId = Number(crewPositionId.get());
    if (positionId) {
      const selectedPosition = crewPositionOptions.find(opt => opt.id === positionId);
      if (selectedPosition) {
        getFullObjectPosition.set({ ...selectedPosition });
      }
    } else {
      getFullObjectPosition.set(null);
    }
  }, [crewPositionId.get(), crewPositionOptions]);

  const handleSaveCrewMember = () => {
    const user = getFullObjectUser.get();
    const position = getFullObjectPosition.get();

    if (user && position) {
      const success = onAddCrewMember(user, position);
      if (success) {
        userId.set(null);
        crewPositionId.set(null);
        getFullObjectUser.set(null);
        getFullObjectPosition.set(null);
      }
    }
  }

  return (
    <>
      <Grid item xs={2}>
        <FilterAutocompleteFetch
          label={translate('User')}
          urlFetch={'/user/get'}
          dataRequest={'user'}
          size={'small'}
          disabled={disabledFields}
          onFetchChange={(user: number | null) => userId?.set(user)}
          resetValues={resetValues?.get()}
          focusChange={focusChange}
          valueId={userId?.get() ? Number(userId?.get()) : undefined}
          getFullObject={getFullObjectUser}
          error={errorField}
        />
      </Grid>
      <Grid item xs={2}>
        <SelectBasic
          state={crewPositionId}
          options={crewPositionOptions}
          placeholder={translate('position')}
          disabled={disabledFields}
          size={'small'}
        />
      </Grid>

      <Grid item xs={2}>
        <Button
          variant="contained"
          disabled={!crewPositionId.get() || !userId.get() || errorField || disabledFields}
          onClick={() => handleSaveCrewMember()}
          sx={{ width: '100%', height: '100%' }}
        >
          {translate('Add')}
        </Button>
      </Grid>
    </>
  )
}